/**
 * Stock as a screen shows it (Phase 2 W4-6; plan §14.3, §20 "How the offline pages work"; D287).
 *
 * The same page reads it from the API in the admin web and from the PC's own copy of the server's
 * data in Dob Desktop (which works without internet). Both sources end in the views below, built by
 * these functions only, so a contract test can check they give the same view for the same data
 * (apps/api/test/local-copy.test.ts).
 *
 * - **The stock at a location** (`StockView`): each item's quantity and, for a person who may see
 *   costs, its value; its batches. On a PC, the commands not in the copy yet (D288) are added on
 *   top and the rows they touch are marked provisional («مؤقت»): the quantity is what it will be
 *   once they arrive; the value is the pool's own rule applied to them (`applyToPool`), as the
 *   server will apply it if nothing else happens first — the server alone decides it (plan §14.5).
 * - **The waste form's choices** (`WasteOptions`): the items and a location's batches, with no
 *   quantity at all (owner O60: the waste screens never show what is available).
 */
import { z } from 'zod';

import { type PendingStockCommand, pendingTakes } from './available.ts';
import { applyToPool, StockPoolError, type StockPool } from './pool.ts';
import { STOCK_UNITS, type StockUnit } from './quantities.ts';

/** Reads a device's copy of the server's data (`SyncClient.list`). */
export interface StockCopy {
  list(entityType: string): readonly Record<string, unknown>[];
}

// ── Shared views ──────────────────────────────────────────────────────────────

const ref = {
  id: z.uuid(),
  code: z.string(),
  nameAr: z.string(),
  nameEn: z.string(),
};

export const StockViewBatchSchema = z.strictObject({
  batchId: z.uuid(),
  code: z.string(),
  expiryDate: z.iso.date(),
  quantity: z.number().int(),
  /** Commands not in the copy yet change it (Dob Desktop only; never in the admin web). */
  provisional: z.boolean(),
});
export type StockViewBatch = z.infer<typeof StockViewBatchSchema>;

export const StockViewRowSchema = z.strictObject({
  item: z.strictObject({ ...ref, stockUnit: z.enum(STOCK_UNITS) }),
  /** In the item's stock unit (g, ml, pieces); below zero after offline issues (plan §14.2). */
  quantity: z.number().int(),
  /** Dinars, only for a person holding `inventory.cost.view` here; null otherwise or unknown. */
  value: z.number().int().nullable(),
  provisional: z.boolean(),
  batches: z.array(StockViewBatchSchema),
  /** When the server last moved this pool (null for a pool only commands on this PC created). */
  updatedAt: z.iso.datetime({ offset: true }).nullable(),
});
export type StockViewRow = z.infer<typeof StockViewRowSchema>;

export const StockViewSchema = z.strictObject({
  location: z.strictObject(ref),
  rows: z.array(StockViewRowSchema),
  /** Commands on top of the server's word (0 in the admin web). */
  provisionalCommands: z.number().int().min(0),
});
export type StockView = z.infer<typeof StockViewSchema>;

export const PickableUnitSchema = z.strictObject({
  id: z.uuid(),
  nameAr: z.string(),
  nameEn: z.string(),
  factor: z.number().int().positive(),
  isDefaultPurchase: z.boolean(),
  isActive: z.boolean(),
});

export const PickableItemSchema = z.strictObject({
  ...ref,
  stockUnit: z.enum(STOCK_UNITS),
  tracksBatches: z.boolean(),
  isActive: z.boolean(),
  /** Its active purchase units (a box of 24, a 25 kg sack), smallest first. */
  units: z.array(PickableUnitSchema),
});
export type PickableItem = z.infer<typeof PickableItemSchema>;

export const WasteBatchOptionSchema = z.strictObject({
  itemId: z.uuid(),
  batchId: z.uuid(),
  code: z.string(),
  expiryDate: z.iso.date(),
});
export type WasteBatchOption = z.infer<typeof WasteBatchOptionSchema>;

export const WasteOptionsSchema = z.strictObject({
  /** Every item, active first (the form marks an inactive one). */
  items: z.array(PickableItemSchema),
  /** The location's batches holding stock on the server: code and expiry only, never a quantity. */
  batches: z.array(WasteBatchOptionSchema),
});
export type WasteOptions = z.infer<typeof WasteOptionsSchema>;

// ── Order (the same on both sides) ────────────────────────────────────────────

const byNameThenId = (a: { nameAr: string; id: string }, b: { nameAr: string; id: string }) =>
  a.nameAr.localeCompare(b.nameAr, 'ar') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const plain = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

function sortRows(rows: StockViewRow[]): StockViewRow[] {
  for (const r of rows)
    r.batches.sort(
      (a, b) =>
        plain(a.expiryDate, b.expiryDate) || plain(a.code, b.code) || plain(a.batchId, b.batchId),
    );
  return rows.sort((a, b) => byNameThenId(a.item, b.item));
}

export function sortPickable(items: readonly PickableItem[]): PickableItem[] {
  return items
    .map((i) => ({
      ...i,
      units: [...i.units].sort((a, b) => a.factor - b.factor || plain(a.id, b.id)),
    }))
    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || byNameThenId(a, b));
}

function sortBatches(batches: WasteBatchOption[]): WasteBatchOption[] {
  return batches.sort(
    (a, b) =>
      plain(a.expiryDate, b.expiryDate) || plain(a.code, b.code) || plain(a.batchId, b.batchId),
  );
}

// ── From the API (admin web) ──────────────────────────────────────────────────

/** `GET /api/inventory/stock?locationId=…` as it answers today (W3). */
export interface ApiStockBalance {
  readonly location: { id: string; code: string; nameAr: string; nameEn: string };
  readonly item: { id: string; code: string; nameAr: string; nameEn: string; stockUnit: StockUnit };
  readonly quantity: number;
  readonly value: number | null;
  readonly batches: readonly {
    batchId: string;
    code: string;
    expiryDate: string;
    quantity: number;
  }[];
  readonly updatedAt: string;
}

/**
 * The view takes each field by name: the location as the page holds it (the server's list of
 * locations, which also says its type) and the API's rows may carry more than the view keeps, and
 * the view is checked strictly. Copying them whole broke «المخزون حسب الموقع» in the admin web for
 * every location (the location's `type`; found in the owner's W5 try, 2026-10-10, D333).
 */
export function stockViewFromApi(
  location: { id: string; code: string; nameAr: string; nameEn: string },
  balances: readonly ApiStockBalance[],
): StockView {
  const rows = balances
    .filter((b) => b.location.id === location.id)
    .map((b) => ({
      item: {
        id: b.item.id,
        code: b.item.code,
        nameAr: b.item.nameAr,
        nameEn: b.item.nameEn,
        stockUnit: b.item.stockUnit,
      },
      quantity: b.quantity,
      value: b.value,
      provisional: false,
      batches: b.batches.map((x) => ({
        batchId: x.batchId,
        code: x.code,
        expiryDate: x.expiryDate,
        quantity: x.quantity,
        provisional: false,
      })),
      updatedAt: new Date(b.updatedAt).toISOString(),
    }));
  return StockViewSchema.parse({
    location: {
      id: location.id,
      code: location.code,
      nameAr: location.nameAr,
      nameEn: location.nameEn,
    },
    rows: sortRows(rows),
    provisionalCommands: 0,
  });
}

/** The waste form's choices from `GET /inventory/items?active=all` and the location's stock. */
export function wasteOptionsFromApi(
  items: readonly PickableItem[],
  balances: readonly ApiStockBalance[],
  locationId: string,
): WasteOptions {
  const batches = balances
    .filter((b) => b.location.id === locationId)
    .flatMap((b) =>
      b.batches.map((x) => ({
        itemId: b.item.id,
        batchId: x.batchId,
        code: x.code,
        expiryDate: x.expiryDate,
      })),
    );
  return WasteOptionsSchema.parse({
    items: sortPickable(items.map((i) => PickableItemSchema.parse(pickFields(i)))),
    batches: sortBatches(batches),
  });
}

function pickFields(i: PickableItem): PickableItem {
  return {
    id: i.id,
    code: i.code,
    nameAr: i.nameAr,
    nameEn: i.nameEn,
    stockUnit: i.stockUnit,
    tracksBatches: i.tracksBatches,
    isActive: i.isActive,
    units: i.units.map((u) => ({
      id: u.id,
      nameAr: u.nameAr,
      nameEn: u.nameEn,
      factor: u.factor,
      isDefaultPurchase: u.isDefaultPurchase,
      isActive: u.isActive,
    })),
  };
}

// ── From a PC's copy (Dob Desktop) ────────────────────────────────────────────

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v));
/** A whole number from the copy (JSON numbers; a bigint column may arrive as text). */
const big = (v: unknown): bigint =>
  typeof v === 'number' ? BigInt(v) : typeof v === 'string' && /^-?\d+$/.test(v) ? BigInt(v) : 0n;

interface CopyItem {
  readonly id: string;
  readonly code: string;
  readonly nameAr: string;
  readonly nameEn: string;
  readonly stockUnit: StockUnit;
  readonly tracksBatches: boolean;
  readonly isActive: boolean;
}

function copyItems(copy: StockCopy): Map<string, CopyItem> {
  const items = new Map<string, CopyItem>();
  for (const r of copy.list('item')) {
    const unit = str(r['stock_unit']);
    if (!(STOCK_UNITS as readonly string[]).includes(unit)) continue;
    items.set(str(r['id']), {
      id: str(r['id']),
      code: str(r['code']),
      nameAr: str(r['name_ar']),
      nameEn: str(r['name_en']),
      stockUnit: unit as StockUnit,
      tracksBatches: r['tracks_batches'] === true,
      isActive: r['is_active'] === true,
    });
  }
  return items;
}

function copyBatches(copy: StockCopy): Map<string, { code: string; expiryDate: string }> {
  return new Map(
    copy
      .list('stock_batch')
      .map((r) => [str(r['id']), { code: str(r['code']), expiryDate: str(r['expiry_date']) }]),
  );
}

/** The items as the waste form offers them, from the copy (PCs receive the whole catalog). */
export function pickableFromCopy(copy: StockCopy): PickableItem[] {
  const units = new Map<string, PickableItem['units'][number][]>();
  for (const r of copy.list('item_unit')) {
    if (r['is_active'] !== true) continue;
    const list = units.get(str(r['item_id'])) ?? [];
    list.push({
      id: str(r['id']),
      nameAr: str(r['name_ar']),
      nameEn: str(r['name_en']),
      factor: num(r['factor']),
      isDefaultPurchase: r['is_default_purchase'] === true,
      isActive: true,
    });
    units.set(str(r['item_id']), list);
  }
  return sortPickable(
    [...copyItems(copy).values()].map((i) => ({ ...i, units: units.get(i.id) ?? [] })),
  );
}

export function wasteOptionsFromCopy(copy: StockCopy, locationId: string): WasteOptions {
  const known = copyBatches(copy);
  const batches: WasteBatchOption[] = [];
  for (const r of copy.list('stock_batch_balance')) {
    if (str(r['location_id']) !== locationId || num(r['quantity']) === 0) continue;
    const batch = known.get(str(r['batch_id']));
    if (!batch) continue;
    batches.push({ itemId: str(r['item_id']), batchId: str(r['batch_id']), ...batch });
  }
  return WasteOptionsSchema.parse({ items: pickableFromCopy(copy), batches: sortBatches(batches) });
}

interface PoolState {
  pool: StockPool;
  /** False once a pending take could not be valued (an unsettled pool): the value is unknown. */
  valued: boolean;
  quantity: number;
  provisional: boolean;
}

/**
 * The stock at the PC's location: the copy's quantities and values, with the commands not in the
 * copy yet on top (`notInCopy`, oldest first). Values only when `seesCost`.
 */
export function stockViewFromCopy(
  copy: StockCopy,
  notInCopy: readonly PendingStockCommand[],
  options: { readonly locationId: string; readonly seesCost: boolean },
): StockView | null {
  const { locationId } = options;
  const locationRow = copy.list('location').find((r) => str(r['id']) === locationId);
  if (!locationRow) return null;
  const items = copyItems(copy);
  const batchesKnown = copyBatches(copy);

  const pools = new Map<string, PoolState>();
  const updated = new Map<string, string>();
  for (const r of copy.list('stock_balance')) {
    if (str(r['location_id']) !== locationId) continue;
    const itemId = str(r['item_id']);
    pools.set(itemId, {
      pool: {
        quantity: big(r['quantity']),
        value: big(r['value']),
        averageValue: big(r['average_value']),
        averageQuantity: big(r['average_quantity']),
      },
      valued: true,
      quantity: num(r['quantity']),
      provisional: false,
    });
    updated.set(itemId, new Date(str(r['updated_at'])).toISOString());
  }
  const batchRows = new Map<string, { itemId: string; quantity: number; provisional: boolean }>();
  for (const r of copy.list('stock_batch_balance')) {
    if (str(r['location_id']) !== locationId) continue;
    batchRows.set(str(r['batch_id']), {
      itemId: str(r['item_id']),
      quantity: num(r['quantity']),
      provisional: false,
    });
  }

  let provisionalCommands = 0;
  const seen = new Set<string>();
  for (const command of notInCopy) {
    const takes = pendingTakes([command]).filter((t) => t.locationId === locationId);
    if (takes.length === 0) continue;
    if (command.commandId !== undefined) {
      if (seen.has(command.commandId)) continue;
      seen.add(command.commandId);
    }
    provisionalCommands += 1;
    for (const take of takes) {
      const state = pools.get(take.itemId) ?? {
        pool: { quantity: 0n, value: 0n, averageValue: 0n, averageQuantity: 0n },
        valued: true,
        quantity: 0,
        provisional: false,
      };
      if (state.valued) {
        try {
          state.pool = applyToPool(state.pool, {
            kind: 'out',
            quantity: BigInt(take.quantity),
          }).pool;
        } catch (error) {
          if (!(error instanceof StockPoolError)) throw error;
          state.valued = false;
        }
      }
      state.quantity -= take.quantity;
      state.provisional = true;
      pools.set(take.itemId, state);
      if (take.batchId !== null) {
        const batch = batchRows.get(take.batchId) ?? {
          itemId: take.itemId,
          quantity: 0,
          provisional: false,
        };
        batch.quantity -= take.quantity;
        batch.provisional = true;
        batchRows.set(take.batchId, batch);
      }
    }
  }

  const rows: StockViewRow[] = [];
  for (const [itemId, state] of pools) {
    const item = items.get(itemId);
    if (!item) continue;
    const batches: StockViewBatch[] = [];
    for (const [batchId, b] of batchRows) {
      if (b.itemId !== itemId || b.quantity === 0) continue;
      const known = batchesKnown.get(batchId);
      if (!known) continue;
      batches.push({ batchId, ...known, quantity: b.quantity, provisional: b.provisional });
    }
    rows.push({
      item: {
        id: item.id,
        code: item.code,
        nameAr: item.nameAr,
        nameEn: item.nameEn,
        stockUnit: item.stockUnit,
      },
      quantity: state.quantity,
      value: options.seesCost && state.valued ? Number(state.pool.value) : null,
      provisional: state.provisional,
      batches,
      updatedAt: updated.get(itemId) ?? null,
    });
  }
  return StockViewSchema.parse({
    location: {
      id: locationId,
      code: str(locationRow['code']),
      nameAr: str(locationRow['name_ar']),
      nameEn: str(locationRow['name_en']),
    },
    rows: sortRows(rows),
    provisionalCommands,
  });
}
